import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Form, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import { BASE_URL } from "../../../../services/Api";
import { authHeader } from "../../../../services/auth-header";
import CommonSalesEnquiryFields from '../CommonSalesEnquiryFields';
import debounce from 'lodash/debounce';

function UpdatePumpDomesticSalesEnquiry() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [status, setstatus] = useState('');
  const [enquiryno, setenquiryno] = useState('');
  const [enquirydate, setEnquiryDate] = useState('');
  const [customermaterialcode, setcustomermaterialcode] = useState('');
  const [customerenquirynumber, setcustomerenquirynumber] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
  const [rows, setRows] = useState([]);
  const [commonFormData, setCommonFormData] = useState({});
  const [formLoaded, setFormLoaded] = useState(false);

  const unitOptions = [
    { value: "", label: "Select UOM" },
    { value: "bot", label: "Bot" },
    { value: "ctn", label: "Ctn" },
    { value: "days", label: "Days" },
    { value: "ft", label: "Ft" },
    { value: "hrs", label: "Hrs" },
    { value: "kg", label: "Kg" },
    { value: "ltr", label: "Ltr" },
    { value: "mtr", label: "Mtr" },
    { value: "nos", label: "Nos" },
    { value: "pil", label: "Pil" },
    { value: "pkt", label: "Pkt" },
    { value: "set", label: "Set" },
    { value: "pcs", label: "Pcs" }
  ];



  useEffect(() => {
    async function fetchEnquiry() {
      try {
        const response = await axios.get(`${BASE_URL}enquiry/findById/${id}`, {
          headers: authHeader()
        });

        const data = response.data;
        setstatus(data.status);
        setenquiryno(data.enquiryno);
        setEnquiryDate(data.enquirydate?.split("T")[0] || '');
        setcustomermaterialcode(data.customermaterialcode || '');
        setcustomerenquirynumber(data.customerenquirynumber || '');

        // Set products as rows
        const productRows = data.enquiryItems?.map(item => ({
          description: item.product.description,
          value: item.product.id,
          label: item.product.description,
          quantity: item.quantity,
          partno: item.partno,
          hsnsacno: item.hsnsacno,
          itemcode: item.itemcode,
          serialnumber: item.serialnumber,
          modelnumber: item.modelnumber,
          tagnumber: item.tagnumber,
          moc: item.moc,
          uom: item.uom,
          productremarks: item.productremarks || "",
        })) || [];

        setRows(productRows);

        setCommonFormData({
          customer: {
            value: data.customer?.id,
            label: data.customer?.companyname,
          },
          customerDetails: {
            contactperson: data.contactperson,
            contactno: data.contactno,
            email: data.email,
          },
          customerMultipleAddress: {
            value: data.customeraddress?.id,
            label: data.customeraddress?.customeraddress,
          },
        });

        setFormLoaded(true);
      } catch (error) {
        Swal.fire("Error", "Failed to fetch enquiry details", "error");
      }
    }

    fetchEnquiry();
  }, [id]);

  const handleProductSearchChange = debounce((inputValue) => {
    setProductSearch(encodeURIComponent(inputValue || " "));
  }, 300);

  const getProductData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
        { headers: authHeader() }
      );
      const products = response.data?.Index || response.data?.data || [];
      const mapped = products.map(product => ({
        value: product.id,
        label: `${product.description} - ${product.serialnumber} - ${product.partno}`,
        ...product,
        uom: product.unitofmeasurement || unitOptions[0].value
      }));
      setProductOptions(mapped);
    } catch (error) {
      console.error('Product fetch error:', error);
    }
  };

  useEffect(() => {
    getProductData();
  }, [productSearch]);

  const handleProductChange = (selectedOption) => {
    if (!selectedOption) return;
    setSelectedProduct(selectedOption);
    const newRow = {
      description: selectedOption.description,
      value: selectedOption.value,
      label: selectedOption.label,
      quantity: '',
      partno: selectedOption.partno,
      hsnsacno: selectedOption.hsnsacno,
      itemcode: selectedOption.itemcode,
      serialnumber: selectedOption.serialnumber,
      modelnumber: selectedOption.modelnumber,
      tagnumber: selectedOption.tagnumber,
      moc: selectedOption.moc,
      uom: selectedOption.uom || unitOptions[0].value,
      productremarks: "",
    };
    setRows([...rows, newRow]);
  };

  const handleRowChange = (index, key, value) => {
    const updated = [...rows];
    updated[index][key] = value;
    setRows(updated);
  };

  const handleQuantityChange = (index, value) => {
    const updated = [...rows];
    updated[index].quantity = value;
    setRows(updated);
  };

  const removeRow = (index) => {
    const updated = [...rows];
    updated.splice(index, 1);
    setRows(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      customer,
      customerDetails,
      customerMultipleAddress
    } = commonFormData;

    if (!customer || !customerMultipleAddress || rows.length === 0) {
      Swal.fire("Missing Info", "Please complete all required fields", "warning");
      return;
    }

    const enquiryItems = rows.map(row => ({
      product: { id: row.value },
      quantity: row.quantity,
      itemcode: row.itemcode || '',
      hsnsacno: row.hsnsacno || '',
      partno: row.partno || '',
      uom: row.uom || '',
      serialnumber: row.serialnumber || '',
      modelnumber: row.modelnumber || '',
      tagnumber: row.tagnumber || '',
      productremarks: row.productremarks || '',
      moc: row.moc || '',
    }));

    const payload = {
      id,
      status,
      enquiryItems,
      enquirytype: "Pump Spare",
      enquirycategory: "Domestic",
      customer: { id: customer.value },
      contactno: customerDetails.contactno,
      contactperson: customerDetails.contactperson,
      email: customerDetails.email,
      customeraddress: { id: customerMultipleAddress.value },
      enquirydate,
      customerenquirynumber,
      enquiryno,
      customermaterialcode,
    };

    try {
      await axios.put(`${BASE_URL}enquiry/update/${id}`, payload, {
        headers: authHeader()
      });
      Swal.fire("Success", "Enquiry updated successfully", "success");
      navigate("/salesEnquiryIndex/1");
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Update failed", "error");
    }
  };

  if (!formLoaded) return <p>Loading...</p>;

  return (
    <div className="container">
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Enquiry Number:</Form.Label>
              <Form.Control
                type="text"
                value={enquiryno}
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Enquiry Date *</Form.Label>
              <Form.Control
                type="date"
                value={enquirydate}
                onChange={e => setEnquiryDate(e.target.value)}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Customer Material Code:</Form.Label>
              <Form.Control
                type="text"
                value={customermaterialcode}
                onChange={e => setcustomermaterialcode(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Customer Enquiry No.:</Form.Label>
              <Form.Control
                type="text"
                value={customerenquirynumber}
                onChange={e => setcustomerenquirynumber(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <CommonSalesEnquiryFields onDataChange={setCommonFormData} initialData={commonFormData} />

        <Form.Group>
          <Form.Label>Select Product</Form.Label>
          <Select
            value={selectedProduct}
            onInputChange={handleProductSearchChange}
            onChange={handleProductChange}
            options={productOptions}
            isClearable
            placeholder="Search product"
          />
        </Form.Group>

        <div className="table-responsive mt-3">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Product Description</th>
                <th>Serial No.</th>
                <th>Model No.</th>
                <th>Tag No.</th>
                <th>Part No.</th>
                <th>MOC</th>
                <th>HSN Code</th>
                <th>Product Code</th>
                <th>Quantity</th>
                <th>UOM</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    {row.description}
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={row.productremarks}
                      onChange={(e) => handleRowChange(index, 'productremarks', e.target.value)}
                      placeholder="Remarks"
                    />
                  </td>
                  <td>{row.serialnumber}</td>
                  <td>{row.modelnumber}</td>
                  <td>{row.tagnumber}</td>
                  <td>{row.partno}</td>
                  <td>{row.moc}</td>
                  <td>{row.hsnsacno}</td>
                  <td>{row.itemcode}</td>
                  <td>
                    <Form.Control
                      type="number"
                      min="1"
                      value={row.quantity}
                      onChange={(e) => handleQuantityChange(index, e.target.value)}
                      required
                    />
                  </td>
                  <td>
                    <Select
                      className='uom'
                      value={unitOptions.find(opt => opt.value === row.uom)}
                      onChange={(selected) => handleRowChange(index, 'uom', selected.value)}
                      options={unitOptions}
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeRow(index)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <button type="submit" className="btn btn-success mt-3">UPDATE</button>
      </Form>
    </div>
  );
}

export default UpdatePumpDomesticSalesEnquiry;